/**
 * EducationCard molecule — single education entry with inline edit.
 *
 * Symmetric with ExperienceCard. Read mode: institution (title, body-md semibold)
 * with a GraduationCap icon, degree + field of study (body-sm), and a date range
 * (body-sm muted) with a Calendar icon — showing "Present" / "En cours" when
 * studies are ongoing. Icon-only edit + delete Buttons appear on hover.
 * Edit mode: inline form with institution/degree/field, a start + end date range
 * with a "Currently studying" toggle (mirroring ExperienceCard's isOngoing handling),
 * Save and "Discard changes" action buttons.
 *
 * All icon-only buttons have a title prop set to the corresponding i18n key.
 * No hardcoded strings, no gradients, no emojis.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Trash2, GraduationCap, Calendar } from 'lucide-react';
import { Typography } from '@/components/ui/atoms/Typography';
import { Button } from '@/components/ui/atoms/Button';
import { Input } from '@/components/ui/atoms/Input';
import { DatePicker } from '@/components/ui/date-picker';
import type { TalentEducation } from '@/types/talent.types';

export interface EducationCardProps {
  education: TalentEducation;
  onSave?: (updated: TalentEducation) => void;
  onDelete?: (id: string) => void;
  /** Start in edit mode (for newly created entries) */
  initialEditing?: boolean;
}

function formatDateRange(
  ongoingLabel: string,
  startDate?: string,
  endDate?: string,
  isOngoing?: boolean,
): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const start = startDate ? fmt(startDate) : '';
  if (isOngoing) return start ? `${start} — ${ongoingLabel}` : ongoingLabel;
  if (start && endDate) return `${start} — ${fmt(endDate)}`;
  return start || (endDate ? fmt(endDate) : '');
}

export const EducationCard: React.FC<EducationCardProps> = ({
  education,
  onSave,
  onDelete,
  initialEditing = false,
}) => {
  const { t } = useTranslation('talentPool');
  const [isEditing, setIsEditing] = useState(initialEditing);
  const [form, setForm] = useState<TalentEducation>(education);

  const handleSave = () => {
    onSave?.(form);
    setIsEditing(false);
  };

  const handleDiscard = () => {
    // Newly added (never-saved) cards are temporary UI state — remove them entirely.
    // Parent wires onDelete to clear the draft without an API call.
    if (initialEditing) {
      onDelete?.(education.id);
      return;
    }
    setForm(education);
    setIsEditing(false);
  };

  const handleChange = (
    field: keyof TalentEducation,
    value: string | boolean | undefined,
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (isEditing) {
    return (
      <div className="rounded-lg border border-light-lila/40 bg-light p-4 flex flex-col gap-3">
        <Input
          type="text"
          value={form.institution}
          onChange={(e) => handleChange('institution', e.target.value)}
          placeholder={t('detail.fields.institutionPlaceholder')}
        />
        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              type="text"
              value={form.degree ?? ''}
              onChange={(e) => handleChange('degree', e.target.value || undefined)}
              placeholder={t('detail.fields.degreePlaceholder')}
            />
          </div>
          <div className="flex-1">
            <Input
              type="text"
              value={form.fieldOfStudy ?? ''}
              onChange={(e) => handleChange('fieldOfStudy', e.target.value || undefined)}
              placeholder={t('detail.fields.fieldOfStudyPlaceholder')}
            />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="flex-1">
            <DatePicker
              value={form.startDate}
              onChange={(v) => handleChange('startDate', v)}
              displayFormat="MMM yyyy"
              placeholder={t('detail.fields.startDatePlaceholder')}
            />
          </div>
          <div className="flex-1">
            <DatePicker
              value={form.endDate}
              onChange={(v) => handleChange('endDate', v)}
              disabled={form.isOngoing}
              displayFormat="MMM yyyy"
              placeholder={t('detail.fields.endDatePlaceholder')}
            />
          </div>
        </div>
        <label className="flex items-center justify-end gap-2 text-sm text-dark-invirtus/80 cursor-pointer">
          {t('detail.fields.currentlyStudying')}
          <input
            type="checkbox"
            checked={form.isOngoing ?? false}
            onChange={(e) => handleChange('isOngoing', e.target.checked)}
            className="rounded border-light-lila"
          />
        </label>
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            type="outlined"
            intent="secondary"
            size="sm"
            label={t('detail.cancelEdit')}
            onClick={handleDiscard}
          />
          <Button
            type="contained"
            intent="primary"
            size="sm"
            label={t('detail.saveSection')}
            onClick={handleSave}
          />
        </div>
      </div>
    );
  }

  const subtitle = [education.degree, education.fieldOfStudy].filter(Boolean).join(' — ');
  const dateRange = formatDateRange(
    t('detail.edu.ongoing'),
    education.startDate,
    education.endDate,
    education.isOngoing,
  );

  return (
    <div className="rounded-lg border border-light-lila/40 bg-light p-4 flex gap-3 group">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <GraduationCap size={15} className="shrink-0 text-dark-invirtus/60" />
          <Typography variant="body-md" weight="semibold" color="primary">
            {education.institution}
          </Typography>
        </div>
        {subtitle && (
          <Typography variant="body-sm" color="secondary" className="mt-0.5">
            {subtitle}
          </Typography>
        )}
        {dateRange && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <Calendar size={13} className="shrink-0 text-dark-invirtus/45" />
            <Typography variant="body-sm" color="muted">
              {dateRange}
            </Typography>
          </div>
        )}
      </div>
      <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<Pencil />}
          title={t('detail.editEducation')}
          onClick={() => setIsEditing(true)}
        />
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<Trash2 />}
          title={t('detail.deleteEducation')}
          onClick={() => onDelete?.(education.id)}
        />
      </div>
    </div>
  );
};

EducationCard.displayName = 'EducationCard';
